import React, { useMemo, useState } from 'react';
import {
  crmEntryShortlisted,
  getCrmEntryForHandle,
  PERF_HUB_SHORTLIST_INTENT_HELP,
} from './perfHubShortlistTags';
import { PERF_HUB_TREND_HELP, trendTooltip } from './perfHubIntelligenceHelp';
import PerfHubCreatorSelectToolbar from './PerfHubCreatorSelectToolbar';
import PerfHubSectionInfo from './PerfHubSectionInfo';
import { PERF_HUB_OVERVIEW_HELP } from './perfHubOverviewHelp';
import { downloadCreatorIntelligenceCsv } from './perfHubCreatorIntelligenceExport';
import { filterIntelCreators, INTEL_FILTERS_FALLBACK } from './perfHubIntelFilter';

function TrendGlyph({ trend }) {
  const tip = trendTooltip(trend);
  if (trend === 'improving') {
    return (
      <span className="ph-intel-trend ph-intel-trend--up" title={tip} aria-label={tip}>
        ↑
      </span>
    );
  }
  if (trend === 'declining') {
    return (
      <span className="ph-intel-trend ph-intel-trend--down" title={tip} aria-label={tip}>
        ↓
      </span>
    );
  }
  if (trend === 'flat') {
    return (
      <span className="ph-intel-trend ph-intel-trend--flat" title={tip} aria-label={tip}>
        →
      </span>
    );
  }
  return (
    <span className="ph-intel-trend ph-intel-trend--na" title={tip} aria-label={tip}>
      —
    </span>
  );
}

function QualityPill({ band }) {
  const labels = {
    high: 'High quality',
    medium: 'Medium',
    low: 'Low',
    low_confidence: 'Low data',
  };
  const cls = band || 'low_confidence';
  return <span className={`ph-intel-quality ph-intel-quality--${cls}`}>{labels[cls] || band}</span>;
}

function BadgeRow({ badges }) {
  if (!badges?.length) return null;
  const labels = {
    top_performer: 'Top performer',
    best_value: 'Best value',
    ugc_suggested: 'UGC suggested',
    phase2_suggested: 'Phase 2',
    needs_connection: 'Not connected',
    needs_metrics: 'No metrics yet',
  };
  return (
    <div className="ph-intel-badges">
      {badges.slice(0, 4).map((b) => (
        <span key={b} className={`ph-intel-badge ph-intel-badge--${b}`}>
          {labels[b] || b}
        </span>
      ))}
    </div>
  );
}

/**
 * Creator Intelligence panel — scorecards, filters, shortlist entry.
 */
export default function PerfHubCreatorIntelligence({
  intelligence,
  crmList,
  busy,
  onOpenShortlist,
  formatNumber,
  selectedRowIds,
  onToggleRowSelect,
  onSelectAllVisible,
  onClearSelection,
  onBulkShortlist,
  campaignName,
  exportEnabled = true,
}) {
  const [filterId, setFilterId] = useState('all');
  const creators = intelligence?.creators || [];
  const filters = intelligence?.filters?.length ? intelligence.filters : INTEL_FILTERS_FALLBACK;

  const filtered = useMemo(
    () => filterIntelCreators(creators, filterId),
    [creators, filterId],
  );

  const canDownload = exportEnabled && filtered.length > 0;

  const handleDownload = () => {
    if (!canDownload) return;
    downloadCreatorIntelligenceCsv(filtered, {
      campaignName,
      filterId,
      filters,
    });
  };

  const summary = intelligence?.summary || {};
  const selection = selectedRowIds instanceof Set ? selectedRowIds : new Set();
  const visibleIds = useMemo(
    () => filtered.map((c) => c.row_id).filter(Boolean),
    [filtered]
  );
  const selectedVisibleCount = useMemo(
    () => visibleIds.filter((id) => selection.has(id)).length,
    [visibleIds, selection]
  );

  if (!creators.length) {
    return (
      <section className="ph-intel-panel ph-glass-card" aria-labelledby="ph-intel-heading">
        <h3 id="ph-intel-heading" className="ph-intel-title">
          <span className="ph-section-icon" aria-hidden>
            🧠
          </span>
          Creator Intelligence
          <PerfHubSectionInfo label="Creator Intelligence">
            {PERF_HUB_OVERVIEW_HELP.creatorIntelligence}
          </PerfHubSectionInfo>
        </h3>
      </section>
    );
  }

  return (
    <section className="ph-intel-panel ph-glass-card" aria-labelledby="ph-intel-heading">
      <div className="ph-intel-head">
        <div>
          <h3 id="ph-intel-heading" className="ph-intel-title">
            <span className="ph-section-icon" aria-hidden>
              🧠
            </span>
            Creator Intelligence
            <PerfHubSectionInfo label="Creator Intelligence">
              {PERF_HUB_OVERVIEW_HELP.creatorIntelligence}
            </PerfHubSectionInfo>
          </h3>
        </div>
        <div className="ph-intel-head-actions">
          {summary.ugc_suggested_count > 0 || summary.phase2_recommended_count > 0 ? (
            <div className="ph-intel-summary-chips">
              {summary.phase2_recommended_count > 0 && (
                <span className="ph-intel-chip ph-intel-chip--phase2">
                  {summary.phase2_recommended_count} phase 2
                </span>
              )}
              {summary.ugc_suggested_count > 0 && (
                <span className="ph-intel-chip ph-intel-chip--ugc">
                  {summary.ugc_suggested_count} UGC suggested
                </span>
              )}
            </div>
          ) : null}
          <button
            type="button"
            className="btn-outline ph-intel-download-btn"
            disabled={!canDownload}
            onClick={handleDownload}
            title={
              !exportEnabled
                ? 'CSV export requires a paid plan'
                : filtered.length === 0
                  ? 'No rows match this filter'
                  : 'Download Creator Intelligence for the current filter'
            }
          >
            Download CSV {!exportEnabled ? '(paid)' : ''}
          </button>
        </div>
      </div>

      <PerfHubCreatorSelectToolbar
        visibleCount={visibleIds.length}
        selectedCount={selectedVisibleCount}
        onSelectAll={() => onSelectAllVisible?.(visibleIds)}
        onClear={() => onClearSelection?.()}
        onBulkShortlist={() => onBulkShortlist?.()}
        busy={busy}
      />

      <div className="ph-intel-filters" role="tablist" aria-label="Creator filters">
        {filters.map((f) => {
          const tip =
            f.id === 'rebook'
              ? PERF_HUB_SHORTLIST_INTENT_HELP.rebook
              : f.id === 'phase2' || f.id === 'phase-2'
                ? PERF_HUB_SHORTLIST_INTENT_HELP['phase-2']
                : f.id === 'ugc'
                  ? PERF_HUB_SHORTLIST_INTENT_HELP.ugc
                  : f.id === 'improving'
                    ? PERF_HUB_SHORTLIST_INTENT_HELP.improving
                    : undefined;
          return (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={filterId === f.id}
              title={tip}
              className={`ph-intel-filter ${filterId === f.id ? 'active' : ''}`}
              onClick={() => setFilterId(f.id)}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      <div className="ph-table-wrap">
        <table className="funds-table enhanced ph-intel-table">
          <thead>
            <tr>
              <th className="ph-th-check" aria-label="Select" />
              <th>Creator</th>
              <th>Re-book</th>
              <th>UGC</th>
              <th title={PERF_HUB_TREND_HELP.summary}>
                Trend
                <span className="ph-intel-th-hint">↑↓</span>
              </th>
              <th>Quality</th>
              <th>Saves</th>
              <th className="ph-th-actions">Shortlist</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => {
              const crmEntry = getCrmEntryForHandle(crmList, c.instagram_handle);
              const saved = crmEntryShortlisted(crmEntry);
              const rowSelected = selection.has(c.row_id);
              return (
                <tr
                  key={c.row_id}
                  className={[
                    c.phase2_recommended ? 'ph-intel-row--phase2' : '',
                    c.ugc_suggested ? 'ph-intel-row--ugc' : '',
                    rowSelected ? 'ph-creator-row--selected' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <td className="ph-td-check">
                    <input
                      type="checkbox"
                      className="ph-creator-row-check"
                      checked={rowSelected}
                      disabled={busy}
                      aria-label={`Select @${c.instagram_handle}`}
                      onChange={() => onToggleRowSelect?.(c.row_id)}
                    />
                  </td>
                  <td>
                    <strong>@{c.instagram_handle}</strong>
                    <BadgeRow badges={c.badges} />
                  </td>
                  <td>
                    <span className="ph-intel-score">{c.rebook_score ?? '—'}</span>
                  </td>
                  <td>
                    <span className="ph-intel-score ph-intel-score--ugc">{c.ugc_score ?? '—'}</span>
                    {c.ugc_suggested && (
                      <span className="ph-intel-suggested-label">Suggested</span>
                    )}
                  </td>
                  <td>
                    <TrendGlyph trend={c.trend} />
                  </td>
                  <td>
                    <QualityPill band={c.quality_band} />
                  </td>
                  <td>{formatNumber(c.metrics?.saved)}</td>
                  <td className="ph-td-actions">
                    <button
                      type="button"
                      className="btn-outline ph-btn-crm-quick"
                      disabled={busy}
                      onClick={() => onOpenShortlist(c)}
                    >
                      {saved ? 'Update shortlist' : 'Add to shortlist'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!filtered.length && (
        <p className="ph-cell-muted ph-intel-empty-filter">No creators match this filter.</p>
      )}
    </section>
  );
}
